/**
 * Mission Control board types. Mirrors the relational store behind /v1/ops
 * (migration 027), which replaced the single-JSONB-blob board.
 */

export type OpsColumn =
  | "needs_jared" | "inbox" | "triage" | "ready" | "building"
  | "review" | "verify" | "done" | "shipped";

/** Left to right, the order the board renders. Needs-you sits first on purpose. */
export const OPS_COLUMN_ORDER: OpsColumn[] = [
  "needs_jared", "inbox", "triage", "ready", "building", "review", "verify", "done", "shipped"
];

export const OPS_COLUMN_LABELS: Record<OpsColumn, string> = {
  needs_jared: "Needs you",
  inbox: "Inbox",
  triage: "Triage",
  ready: "Ready",
  building: "Building",
  review: "Review",
  verify: "Verify",
  done: "Done (dev)",
  shipped: "Shipped (prod)"
};

export const OPS_COLUMN_HINTS: Record<OpsColumn, string> = {
  needs_jared: "A decision only you can make, or three failed review laps",
  inbox: "Captured, not yet shaped",
  triage: "Sized and assigned a stream, still needs acceptance criteria",
  ready: "Has acceptance criteria. Only these can be claimed",
  building: "Claimed by one worker, in its own worktree",
  review: "PR open, being read by an independent reviewer",
  verify: "Reviewed. Being exercised against the running dev app",
  done: "Live and proven on dev",
  shipped: "Live on production. Only you move a card here"
};

export interface AcceptanceCriterion {
  criterion: string;
  met?: boolean;
}

export interface OpsStream {
  id: string;
  name: string;
  repo: string | null;
  colour: string;
  position: number;
  paused: boolean;
}

export interface OpsCard {
  id: number;
  ref: string;
  streamId: string;
  col: OpsColumn;
  title: string;
  body: string;
  acceptance: AcceptanceCriterion[] | null;
  filesInScope: string[] | null;
  testPlan: string | null;
  priority: number;
  labels: string[];
  source: string;
  supportRefs: string[];
  demandTenants: string[];
  supersedes: string | null;
  planRef: string | null;
  claimedBy: string | null;
  claimedAt: string | null;
  leaseUntil: string | null;
  reviewLaps: number;
  branch: string | null;
  prUrl: string | null;
  ciStatus: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  doneAt: string | null;
}

export interface OpsEvent {
  id: number;
  cardId: number | null;
  cardRef?: string | null;
  actor: string;
  kind: string;
  fromCol: string | null;
  toCol: string | null;
  detail: Record<string, unknown> | null;
  at: string;
}

export interface OpsBoardResponse {
  streams: OpsStream[];
  cards: OpsCard[];
  meta: { paused: boolean; settings: Record<string, unknown> };
  counts: Record<string, number>;
}

/**
 * Priority bands. Scored, not asserted: P0 is a paying tenant who cannot work,
 * and a card improves one band for each DISTINCT tenant who asks for it.
 */
export const PRIORITY_BANDS: { max: number; label: string; tone: string }[] = [
  { max: 10, label: "P0", tone: "danger" },
  { max: 25, label: "P1", tone: "warn" },
  { max: 40, label: "P2", tone: "accent" },
  { max: 60, label: "P3", tone: "info" },
  { max: 100, label: "P4", tone: "" }
];

export function priorityBand(priority: number): { label: string; tone: string } {
  for (const b of PRIORITY_BANDS) if (priority <= b.max) return { label: b.label, tone: b.tone };
  return { label: "P4", tone: "" };
}
